import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos, useRegister } from "@/app/context";
import { IdentityCheckModal } from "@/features/buylist/IdentityCheckModal";
import { call } from "@/lib/api";
import type { ConsignmentIntake } from "@/lib/consignment-types";
import { readSetting, writeSetting } from "@/lib/db";
import { errorText, isApiError } from "@/lib/errors";
import { newIdempotencyKey } from "@/lib/idempotency";

import {
  ConsignorSearch,
  ConsignorSummary,
  consignorBlockers,
  type ChosenConsignor,
} from "./ConsignorPanel";
import { ConsignmentDone } from "./DoneScreen";
import { IntakeLineForm, IntakeLineRow, lineBody, type ConsignLine } from "./IntakeLine";
import { TermsPanel } from "./TermsPanel";

export const CONSIGN_DRAFT_KEY = "consignmentDraft";

interface Draft {
  consignor: ChosenConsignor | null;
  lines: ConsignLine[];
}

/**
 * 対価の総額 for consignment-service's 本人確認 rule engine only (never shown — the total on the
 * documents is the server's `total` after the intake).
 */
function ruleAmount(lines: readonly ConsignLine[]): number {
  return lines.reduce(
    (sum, l) =>
      sum +
      Number.parseInt(l.price || "0", 10) * (l.productType === "sealed_product" ? l.quantity : 1),
    0,
  );
}

/**
 * 委託受付 (T07-32, design/05 §3.5): the consignor (find / new — 本人確認 and the signed agreement
 * are checked first, the button stays off with the reason), the cards (Rank A–D required for raw),
 * the terms (consignment-service's), then one request `POST /pos/consignment-intakes`
 * (Idempotency-Key) → 完了: 委託契約書 / 預り証 to print, a label per card. Online only (the menu
 * says so offline).
 */
export function ConsignmentIntakeScreen() {
  const { t } = useTranslation();
  const { consignment, db } = usePos();
  const register = useRegister();
  const [draft, setDraft] = useState<Draft>({ consignor: null, lines: [] });
  const [loaded, setLoaded] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ConsignmentIntake | null>(null);
  const submitKey = useRef<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    void readSetting<Draft>(db, CONSIGN_DRAFT_KEY, { consignor: null, lines: [] }).then((saved) => {
      setDraft(saved);
      seq.current = saved.lines.length;
      setLoaded(true);
    });
  }, [db]);
  useEffect(() => {
    if (loaded) {
      void writeSetting(db, CONSIGN_DRAFT_KEY, draft);
    }
  }, [db, draft, loaded]);

  const nextKey = () => {
    seq.current += 1;
    return `line-${String(seq.current)}`;
  };

  const consignor = draft.consignor;
  const blockers = consignor ? consignorBlockers(consignor) : [];
  const canSubmit = Boolean(consignor) && blockers.length === 0 && draft.lines.length > 0 && !busy;

  const acceptAgreement = async () => {
    if (!consignor) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await call(() =>
        consignment.POST("/v1/admin/consignors/{consignor_id}/agreement-acceptances", {
          params: {
            path: { consignor_id: consignor.id },
            header: { "Idempotency-Key": newIdempotencyKey() },
          },
          body: { channel: "admin" },
        }),
      );
      setDraft((d) =>
        d.consignor ? { ...d, consignor: { ...d.consignor, agreementAccepted: true } } : d,
      );
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!consignor || !canSubmit) {
      return;
    }
    const locationId = register.data?.register.locationId;
    if (!locationId) {
      return;
    }
    setBusy(true);
    setError(null);
    submitKey.current ??= newIdempotencyKey();
    try {
      const intake = await call(() =>
        consignment.POST("/v1/pos/consignment-intakes", {
          params: { header: { "Idempotency-Key": submitKey.current ?? newIdempotencyKey() } },
          body: {
            consignorId: consignor.id,
            locationId,
            ...(consignor.identityCheckId ? { identityCheckId: consignor.identityCheckId } : {}),
            ...(register.data?.register.id ? { registerId: register.data.register.id } : {}),
            lines: draft.lines.map(lineBody),
          },
        }),
      );
      setDone(intake);
      setDraft({ consignor: null, lines: [] });
      submitKey.current = null;
    } catch (err) {
      if (!isApiError(err, "network") && !(isApiError(err) && err.status >= 500)) {
        submitKey.current = null;
      }
      if (isApiError(err, "identity_check_required") || isApiError(err, "identity_check_expired")) {
        setDraft((d) =>
          d.consignor ? { ...d, consignor: { ...d.consignor, identityVerified: false } } : d,
        );
      }
      if (isApiError(err, "agreement_not_accepted")) {
        setDraft((d) =>
          d.consignor ? { ...d, consignor: { ...d.consignor, agreementAccepted: false } } : d,
        );
      }
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return <ConsignmentDone intake={done} onNext={() => setDone(null)} />;
  }
  if (!loaded) {
    return null;
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto p-3">
      {!consignor ? (
        <div className="mx-auto flex max-w-3xl flex-col gap-3">
          <h1 className="text-2xl font-semibold">{t("nav.consignment")}</h1>
          <ConsignorSearch onChoose={(c) => setDraft((d) => ({ ...d, consignor: c }))} />
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_26rem]">
          <div className="flex min-w-0 flex-col gap-3">
            <ConsignorSummary
              consignor={consignor}
              onIdentity={() => setIdentityOpen(true)}
              onAgreement={() => void acceptAgreement()}
              onChange={() => setDraft((d) => ({ ...d, consignor: null }))}
              busy={busy}
            />
            <ul aria-label={t("consign.lines")} className="rounded-lg border">
              {draft.lines.map((line) => (
                <IntakeLineRow
                  key={line.key}
                  line={line}
                  onRemove={() =>
                    setDraft((d) => ({ ...d, lines: d.lines.filter((l) => l.key !== line.key) }))
                  }
                />
              ))}
              {draft.lines.length === 0 ? (
                <li className="p-3 text-muted-foreground">{t("consign.no_lines")}</li>
              ) : null}
            </ul>
            <IntakeLineForm
              onAdd={(line) => setDraft((d) => ({ ...d, lines: [...d.lines, line] }))}
              nextKey={nextKey}
            />
          </div>
          <div className="flex flex-col gap-3">
            <TermsPanel consignorId={consignor.id} />
            <section className="flex flex-col gap-2 rounded-lg border p-3">
              <p className="text-xl font-semibold">
                {t("consign.card_count", { count: draft.lines.length })}
              </p>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={!canSubmit}
                aria-describedby={!canSubmit ? "consign-why" : undefined}
                className="min-h-pos-touch rounded-md bg-primary px-6 text-xl font-semibold text-primary-foreground disabled:opacity-50"
              >
                {busy ? t("app.saving") : t("consign.submit")}
              </button>
              {!canSubmit && !busy ? (
                <p id="consign-why" className="text-pos-problem">
                  {blockers[0]
                    ? t(`consign.blocker_${blockers[0]}`)
                    : draft.lines.length === 0
                      ? t("consign.blocker_no_lines")
                      : ""}
                </p>
              ) : null}
              {error ? (
                <p role="alert" className="text-pos-problem">
                  {error}
                </p>
              ) : null}
            </section>
          </div>
        </div>
      )}
      {consignor ? (
        <IdentityCheckModal
          open={identityOpen}
          onClose={() => setIdentityOpen(false)}
          onDone={(check) => {
            setIdentityOpen(false);
            setDraft((d) =>
              d.consignor
                ? {
                    ...d,
                    consignor: {
                      ...d.consignor,
                      identityVerified: true,
                      identityCheckId: check.identityCheckId,
                    },
                  }
                : d,
            );
          }}
          consignorId={consignor.id}
          amount={ruleAmount(draft.lines)}
          defaultName={consignor.legalName ?? null}
        />
      ) : null}
    </div>
  );
}
